import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Shield, Camera, Brain, Lock, Users, Eye, Hand, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Policy Section Component
const PolicySection = ({ icon: Icon, title, children, testId }) => {
  return (
    <AnimatedSection className="mb-12">
      <div className="flex items-start gap-4" data-testid={testId}>
        <div className="flex-shrink-0 w-12 h-12 bg-purple-900/30 flex items-center justify-center">
          <Icon className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white mb-4">
            {title}
          </h2>
          <div className="text-gray-400 leading-relaxed font-light space-y-3">
            {children}
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0a0a1a]/90 border-b border-purple-900/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/astrodots" className="flex items-center gap-3" data-testid="astrodots-logo">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-10 w-10 object-contain" />
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </a>
          <Button 
            asChild
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-purple-900/30"
            data-testid="back-btn"
          >
            <a href="/astrodots">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to AstroDots
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-[#0a0a1a] border-t border-purple-900/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="flex items-center gap-3 mb-4">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-8 w-8 object-contain" />
            <span className="font-heading text-lg font-bold text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </div>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function AstroDotsPrivacy() {
  const lastUpdated = "January 28, 2026";

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          {/* Hero */}
          <AnimatedSection className="mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-4 block">
              Legal
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6" data-testid="privacy-headline">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-400 font-light mb-4">
              This Privacy Policy applies to the AstroDots mobile application, including Iris Odyssey and Celestial Palmistry features.
            </p>
            <p className="text-sm text-gray-500">
              Last Updated: {lastUpdated}
            </p>
          </AnimatedSection>

          {/* Introduction */}
          <AnimatedSection className="mb-12">
            <p className="text-gray-400 leading-relaxed font-light">
              At AstroDots, we are committed to protecting your privacy. This policy explains how we handle data in our celestial analysis features, including camera-based iris and palm reading functionalities.
            </p>
          </AnimatedSection>

          <div className="h-px bg-purple-900/30 mb-12" />

          {/* Policy Sections */}
          <PolicySection 
            icon={Camera} 
            title="Camera Data Usage (Iris Odyssey & Celestial Palmistry)" 
            testId="policy-camera"
          >
            <p>
              Our Iris Odyssey and Celestial Palmistry features use your device's camera to capture images of your iris and palm for entertainment analysis purposes.
            </p>
            <p>
              <strong className="text-purple-300">Important:</strong> Camera data captured through these features is processed solely for generating your celestial readings and entertainment insights. <strong className="text-purple-300">This data is NOT stored permanently on our servers and is NOT shared with any third parties.</strong>
            </p>
            <p>
              Images are processed in real-time to provide your readings, and the data is immediately discarded after processing. We do not maintain any database of user iris or palm images.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Eye} 
            title="Iris Odyssey Feature" 
            testId="policy-iris"
          >
            <p>
              The Iris Odyssey feature analyzes patterns in your iris for entertainment-based celestial resonance readings. The analysis is performed using on-device processing where possible, and any cloud processing uses anonymized, temporary data streams.
            </p>
            <p>
              <strong className="text-purple-300">No iris images or biometric data are retained after your session ends.</strong>
            </p>
          </PolicySection>

          <PolicySection 
            icon={Hand} 
            title="Celestial Palmistry Feature" 
            testId="policy-palm"
          >
            <p>
              Celestial Palmistry captures images of your palm to identify lines and features for astrological interpretation. Similar to Iris Odyssey, this feature processes data for immediate entertainment analysis only.
            </p>
            <p>
              <strong className="text-purple-300">Palm images are processed transiently and are not stored, archived, or transmitted to third parties.</strong>
            </p>
          </PolicySection>

          <PolicySection 
            icon={Brain} 
            title="AI Processing" 
            testId="policy-ai"
          >
            <p>
              Our AI-powered analysis is designed for entertainment purposes. The AI processes visual data to generate celestial and bio-rhythm insights without creating permanent records or user profiles based on biometric data.
            </p>
            <p>
              <strong className="text-purple-300">The AI does not use your camera data for identity verification, tracking, or any purpose beyond generating your entertainment readings.</strong>
            </p>
          </PolicySection>

          <PolicySection 
            icon={Lock} 
            title="No Third-Party Sharing" 
            testId="policy-no-sharing"
          >
            <p>
              <strong className="text-purple-300">We do not sell, rent, trade, or share your camera data or any derived biometric information with third parties.</strong>
            </p>
            <p>
              Your celestial readings are generated solely for your personal entertainment and self-reflection. We do not monetize user data or engage in data brokering practices.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Shield} 
            title="Data Minimization" 
            testId="policy-data-min"
          >
            <p>
              We follow strict data minimization principles. We only process the camera data necessary to generate your readings, and we retain no permanent records of this visual data.
            </p>
            <p>
              Basic app usage data (non-biometric) may be collected to improve app performance and user experience, but this never includes your iris or palm images.
            </p>
          </PolicySection>

          <PolicySection 
            icon={Users} 
            title="Target Audience" 
            testId="policy-audience"
          >
            <p>
              <strong className="text-purple-300">AstroDots is designed for users aged 18 years and older.</strong> We do not knowingly collect any data from children under 18.
            </p>
          </PolicySection>

          <div className="h-px bg-purple-900/30 mb-12" />

          {/* Contact Section */}
          <AnimatedSection>
            <div className="bg-purple-900/20 border border-purple-500/20 p-8 sm:p-12">
              <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white mb-4">
                Questions?
              </h2>
              <p className="text-gray-400 leading-relaxed font-light mb-6">
                If you have any questions about this Privacy Policy or how we handle your data, please contact our support team.
              </p>
              <Button 
                asChild
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-base font-medium"
                data-testid="contact-cta"
              >
                <a href="/astrodots#support">Contact Support</a>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </main>

      <Footer />
    </div>
  );
}
